import {
	ClockCircleOutlined,
	CloseCircleOutlined,
	CrownOutlined,
	MobileOutlined,
} from "@ant-design/icons";
import {
	useCreateCheckoutSession,
	useMemberships,
} from "@bodhistreams/api/src";
import { VideoBackground } from "@bodhistreams/ui";
import { Button, Card, Col, Result, Row, Space, Typography, theme } from "antd";
import { useNavigate } from "react-router-dom";
import PageLayout from "./PageLayout";

const { Title, Text } = Typography;
const { useToken } = theme;

const SubscriptionCancel = () => {
	const navigate = useNavigate();
	const { token } = useToken();
	const { data: memberships = [] } = useMemberships();
	const createCheckoutSessionMutation = useCreateCheckoutSession();

	const handleSelectPlan = async (membership) => {
		try {
			const response = await createCheckoutSessionMutation.mutateAsync({
				membershipId: membership.id,
				isYearly: false,
			});
			if (response.checkout_url) {
				window.location.href = response.checkout_url;
			}
		} catch (error) {
			console.error("Failed to create checkout session:", error);
		}
	};

	const renderAvailablePlans = () => {
		const paidMemberships = memberships.filter(
			(m) => m.name.toLowerCase() !== "free",
		);

		if (paidMemberships.length === 0) return null;

		return (
			<Card
				style={{
					background: "rgba(255, 255, 255, 0.95)",
					borderRadius: "12px",
					marginTop: "32px",
					maxWidth: "800px",
					margin: "32px auto 0",
				}}
				title={
					<Space>
						<CrownOutlined style={{ color: token.colorWarning }} />
						<Text strong style={{ fontSize: "18px" }}>
							Available Plans
						</Text>
					</Space>
				}
			>
				<Row gutter={[16, 16]}>
					{paidMemberships.map((membership) => (
						<Col xs={24} sm={12} md={8} key={membership.id}>
							<Card
								size="small"
								style={{
									textAlign: "center",
									border: `1px solid ${token.colorBorder}`,
									borderRadius: "8px",
									height: "100%",
								}}
								actions={[
									<Button
										key={`button-${membership.id}`}
										type="primary"
										onClick={() => handleSelectPlan(membership)}
										style={{ width: "90%" }}
									>
										Choose {membership.name}
									</Button>,
								]}
							>
								<div>
									<Title
										level={4}
										style={{ marginBottom: "8px", color: token.colorInfo }}
									>
										{membership.name}
									</Title>
									<div style={{ marginBottom: "16px" }}>
										<Text
											strong
											style={{ fontSize: "24px", color: token.colorSuccess }}
										>
											${parseFloat(membership.price).toFixed(2)}
										</Text>
										<Text style={{ color: token.colorTextSecondary }}>
											/month
										</Text>
									</div>
									<Space
										orientation="vertical"
										size="small"
										style={{ width: "100%" }}
									>
										<div>
											<MobileOutlined
												style={{ color: token.colorInfo, marginRight: "8px" }}
											/>
											<Text style={{ fontSize: "12px" }}>
												{membership.max_simultaneous_devices} device
												{membership.max_simultaneous_devices !== 1 ? "s" : ""}
											</Text>
										</div>
										<div>
											<ClockCircleOutlined
												style={{ color: token.colorPurple, marginRight: "8px" }}
											/>
											<Text style={{ fontSize: "12px" }}>
												{membership.streaming_hours
													? `${membership.streaming_hours} hrs/month`
													: "Unlimited"}
											</Text>
										</div>
									</Space>
								</div>
							</Card>
						</Col>
					))}
				</Row>
			</Card>
		);
	};

	return (
		<PageLayout
			fullWidthContent={
				<VideoBackground
					containerStyle={{
						padding: "40px 24px",
						textAlign: "center",
						minHeight: "100vh",
						display: "flex",
						flexDirection: "column",
						justifyContent: "center",
						alignItems: "center",
					}}
					contentStyle={{
						maxWidth: "900px",
					}}
				>
					<div style={{ width: "100%" }}>
						<Result
							icon={
								<CloseCircleOutlined
									style={{ color: token.colorError, fontSize: "72px" }}
								/>
							}
							status="error"
							title={
								<Title
									level={2}
									style={{ color: token.colorError, marginTop: "16px" }}
								>
									Subscription Cancelled
								</Title>
							}
							subTitle={
								<div>
									<Text
										style={{
											fontSize: "18px",
											display: "block",
											marginBottom: "8px",
										}}
									>
										Your subscription process was cancelled.
									</Text>
									<Text
										style={{
											fontSize: "16px",
											color: token.colorTextSecondary,
										}}
									>
										No charges were made to your account. You can try again
										anytime with any of our plans below.
									</Text>
								</div>
							}
							extra={[
								<Button
									type="primary"
									key="pricing"
									size="large"
									onClick={() => navigate("/#pricing")}
								>
									View All Pricing Plans
								</Button>,
								<Button key="home" size="large" onClick={() => navigate("/")}>
									Back to Home
								</Button>,
							]}
							style={{
								background: "rgba(255, 255, 255, 0.95)",
								borderRadius: "12px",
								padding: "40px 32px",
							}}
						/>

						{renderAvailablePlans()}
					</div>
				</VideoBackground>
			}
		/>
	);
};

export default SubscriptionCancel;
